import { ageLabel } from '../../../utils/records';
import Table from "../../../components/RecordTable";
import { useSearchParams } from "react-router-dom";
import useAutoRefresh from "../../../hooks/useAutoRefresh";
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Drawer,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Avatar,
  Typography,
  Descriptions,
  Divider,
  Badge,
  Tooltip,
  Row,
  Col,
  Statistic,
  List,
  Image,
  Alert,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  EyeOutlined,
  CopyOutlined,
  StopOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  GiftOutlined,
  CrownOutlined,
  CalendarOutlined,
  FileOutlined,
  HeartOutlined,
  IdcardOutlined,
} from "@ant-design/icons";
import { userAPI, doctorAPI, patientAPI, doctorSettingsAPI } from "../../../services/api";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;
const { Option } = Select;

const roleColor = (role) => {
  if (role === "superadmin") return "purple";
  if (role === "doctor") return "blue";
  if (role === "labadmin") return "cyan";
  return "green";
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Search / filter state
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("q") || "");
  useEffect(() => { setSearch(searchParams.get("q") || ""); setPagination(p => ({ ...p, current: 1 })); }, [searchParams]);
  const [searchMemberId, setSearchMemberId] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMembership, setFilterMembership] = useState("");
  const [filterCategories, setFilterCategories] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);

  // View drawer state
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [viewDoctorProfile, setViewDoctorProfile] = useState(null);
  const [viewPatientProfile, setViewPatientProfile] = useState(null);
  const [profileError, setProfileError] = useState("");
  const [viewLoading, setViewLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: pagination.current, limit: pagination.pageSize };
      if (search.trim()) params.search = search.trim();
      if (searchMemberId.trim()) params.membershipId = searchMemberId.trim();
      if (filterRole) params.role = filterRole;
      if (filterStatus !== "") params.isActive = filterStatus;
      if (filterMembership !== "") params.hasMembership = filterMembership;
      if (filterCategories.length > 0) params.categories = filterCategories.join(",");
      const response = await userAPI.getAll(params);
      setUsers(response.data.users || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.pagination?.total || 0,
      }));
    } catch (error) {
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, search, searchMemberId, filterRole, filterStatus, filterMembership, filterCategories]);

  useAutoRefresh(() => fetchUsers());

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    doctorSettingsAPI.getSettings().then(res => {
      const cats = res?.data?.data?.categories || res?.data?.categories || [];
      setAvailableCategories(cats);
    }).catch(() => {});
  }, []);

  const handleApplyFilters = () => {
    setPagination(p => ({ ...p, current: 1 }));
    fetchUsers();
  };

  const handleResetFilters = () => {
    setSearch("");
    setSearchMemberId("");
    setFilterRole("");
    setFilterStatus("");
    setFilterMembership("");
    setFilterCategories([]);
    setPagination(p => ({ ...p, current: 1 }));
  };

  const openViewDrawer = async (record) => {
    setViewDrawerOpen(true);
    setViewUser(null);
    setViewDoctorProfile(null);
    setViewPatientProfile(null);
    setViewLoading(true);
    setProfileError("");
    try {
      const [userRes, doctorRes, patientRes] = await Promise.allSettled([
        userAPI.getById(record._id),
        record.role === "doctor" ? doctorAPI.getByUserId(record._id) : Promise.resolve(null),
        record.role === "patient" ? patientAPI.getByUserId(record._id) : Promise.resolve(null),
      ]);
      if (userRes.status === "fulfilled") setViewUser(userRes.value?.data);
      else { message.error("Failed to load user details"); setViewDrawerOpen(false); }
      if (doctorRes.status === "fulfilled" && doctorRes.value) {
        setViewDoctorProfile(doctorRes.value?.data?.data || doctorRes.value?.data || null);
      }
      if (patientRes.status === "rejected") setProfileError(patientRes.reason?.response?.status === 404 ? "This patient has not completed a medical profile yet." : "Medical profile could not be loaded. Close and reopen to retry.");
      if (patientRes.status === "fulfilled" && patientRes.value) {
        setViewPatientProfile(patientRes.value?.data || null);
      }
    } finally {
      setViewLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!viewUser) return;
    setActionLoading(true);
    try {
      const res = await userAPI.suspend(viewUser._id);
      const updated = res.data;
      setViewUser((prev) => ({ ...prev, isActive: updated.isActive }));
      // Update table row too
      setUsers((prev) =>
        prev.map((u) =>
          u._id === updated._id ? { ...u, isActive: updated.isActive } : u
        )
      );
      message.success(
        updated.isActive ? "User unsuspended successfully" : "User suspended successfully"
      );
    } catch (err) {
      message.error(err?.response?.data?.message || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateReferralCode = async () => {
    if (!viewUser) return;
    setActionLoading(true);
    try {
      const res = await userAPI.generateReferralCode(viewUser._id);
      const { referralCode } = res.data;
      setViewUser((prev) => ({
        ...prev,
        referralStats: { ...(prev.referralStats || {}), referralCode, totalReferrals: prev.referralStats?.totalReferrals ?? 0, referredUsers: prev.referralStats?.referredUsers ?? [] },
      }));
      message.success("Referral code generated");
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to generate code");
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!viewUser) return;
    setActionLoading(true);
    try {
      const res = await userAPI.verifyEmail(viewUser._id);
      const { isEmailVerified } = res.data;
      setViewUser((prev) => ({ ...prev, isEmailVerified }));
      setUsers((prev) =>
        prev.map((u) =>
          u._id === viewUser._id ? { ...u, isEmailVerified } : u
        )
      );
      message.success(res.message);
    } catch (err) {
      message.error(err?.response?.data?.message || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle email verification directly from the table row
  const [togglingEmailId, setTogglingEmailId] = useState(null);
  const toggleEmailVerified = async (record) => {
    setTogglingEmailId(record._id);
    try {
      const res = await userAPI.verifyEmail(record._id);
      const { isEmailVerified } = res.data;
      setUsers((prev) =>
        prev.map((u) => (u._id === record._id ? { ...u, isEmailVerified } : u))
      );
      if (viewUser?._id === record._id) {
        setViewUser((prev) => ({ ...prev, isEmailVerified }));
      }
      message.success(res.message);
    } catch (err) {
      message.error(err?.response?.data?.message || "Action failed");
    } finally {
      setTogglingEmailId(null);
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingUser) {
        // Remove password if left blank during edit
        const payload = { ...values };
        if (!payload.password) delete payload.password;
        await userAPI.update(editingUser._id, payload);
        message.success("User updated successfully");
      } else {
        await userAPI.create(values);
        message.success("User created successfully");
      }
      setModalVisible(false);
      form.resetFields();
      fetchUsers();
    } catch (error) {
      message.error(error?.response?.data?.message || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await userAPI.delete(id);
      message.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      message.error("Delete failed");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Copied to clipboard");
  };

  const columns = [
    { title: "User / patient ID", dataIndex: "_id", width: 180, render: (id, row) => <Text code copyable>{row.publicId || id}</Text> },
    {
      title: "User",
      key: "user",
      width: 260,
      render: (_, record) => (
        <Space size={12}>
          <Avatar
            size={44}
            src={record.profileImage}
            icon={<UserOutlined />}
            style={{ backgroundColor: "#0A6EBD", flexShrink: 0 }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{record.name}</div>
            <div style={{ fontSize: 12, color: "#888", wordBreak: "break-all" }}>
              {record.email}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 140,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 110,
      render: (role) => (
        <Tag color={roleColor(role)} style={{ margin: 0 }}>
          {role?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Category",
      key: "category",
      width: 160,
      render: (_, record) => {
        const cats = Array.isArray(record.categories) && record.categories.length > 0
          ? record.categories
          : record.category
          ? [record.category]
          : [];
        return cats.length > 0 ? (
          <Space size={[0, 4]} wrap>
            {cats.map(c => <Tag key={c} color="orange" style={{ margin: 0 }}>{c}</Tag>)}
          </Space>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 200,
      render: (_, record) => (
        <Space size={4} wrap>
          <Tag color={record.isActive !== false ? "success" : "error"} style={{ margin: 0 }}>
            {record.isActive !== false ? "Active" : "Suspended"}
          </Tag>
          <Tooltip title={record.isEmailVerified ? "Click to unverify email" : "Click to verify email"}>
            <Tag
              color={record.isEmailVerified ? "blue" : "orange"}
              style={{ margin: 0, cursor: "pointer" }}
              icon={togglingEmailId === record._id ? null : (record.isEmailVerified ? <CheckCircleOutlined /> : null)}
              onClick={() => toggleEmailVerified(record)}
            >
              {togglingEmailId === record._id
                ? "..."
                : record.isEmailVerified
                ? "Email Verified"
                : "Email Unverified"}
            </Tag>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Membership",
      key: "membership",
      width: 160,
      render: (_, record) => {
        const mem = record.membership;
        if (mem?.isActive) {
          return (
            <Space orientation="vertical" size={2}>
              <Tag color="gold" icon={<CrownOutlined />} style={{ margin: 0 }}>
                {mem.planName || "Active"}
              </Tag>
              {mem.membershipId && (
                <Tag color="geekblue" style={{ margin: 0, fontSize: 11 }}>
                  {mem.membershipId}
                </Tag>
              )}
            </Space>
          );
        }
        return <Tag color="default" style={{ margin: 0 }}>No Plan</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      fixed: "right",
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="View Details">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => openViewDrawer(record)}
              style={{ padding: "4px 8px" }}
            >
              View
            </Button>
          </Tooltip>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingUser(record);
              form.setFieldsValue(record);
              setModalVisible(true);
            }}
            style={{ padding: "4px 8px" }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete User"
            description="Are you sure you want to delete this user?"
            onConfirm={() => handleDelete(record._id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              style={{ padding: "4px 8px" }}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const referralStats = viewUser?.referralStats;

  return (
    <div>
      <div
        style={{
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0 }}>
            User Management
          </Title>
          <div style={{ color: "#666", fontSize: 14, marginTop: 4 }}>
            Manage system users and their roles
          </div>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchUsers} loading={loading}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => {
              form.resetFields();
              setEditingUser(null);
              setModalVisible(true);
            }}
          >
            Add User
          </Button>
        </Space>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={5}>
            <Input
              placeholder="Name / Email / Phone"
              prefix={<SearchOutlined />}
              value={search}
              onChange={e => setSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder="Member ID (NPH-...)"
              prefix={<IdcardOutlined />}
              value={searchMemberId}
              onChange={e => setSearchMemberId(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select value={filterRole} onChange={setFilterRole} style={{ width: "100%" }} placeholder="All Roles" allowClear>
              <Option value="">All Roles</Option>
              <Option value="patient">Patient</Option>
              <Option value="doctor">Doctor</Option>
              <Option value="labadmin">Lab Admin</Option>
              <Option value="superadmin">Super Admin</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select value={filterStatus} onChange={setFilterStatus} style={{ width: "100%" }} placeholder="All Status" allowClear>
              <Option value="">All Status</Option>
              <Option value="true">Active</Option>
              <Option value="false">Suspended</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select value={filterMembership} onChange={setFilterMembership} style={{ width: "100%" }} placeholder="Membership" allowClear>
              <Option value="">All</Option>
              <Option value="true">Has Membership</Option>
              <Option value="false">No Membership</Option>
            </Select>
          </Col>
          {availableCategories.length > 0 && (
            <Col xs={24} sm={12} md={5}>
              <Select
                mode="multiple"
                value={filterCategories}
                onChange={setFilterCategories}
                style={{ width: "100%" }}
                placeholder="Filter by Category"
                maxTagCount={2}
                allowClear
              >
                {availableCategories.map(c => (
                  <Option key={c} value={c}>{c}</Option>
                ))}
              </Select>
            </Col>
          )}
          <Col>
            <Space>
              <Button type="primary" onClick={handleApplyFilters}>Search</Button>
              <Button onClick={handleResetFilters}>Reset</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="_id"
          loading={loading}
          pagination={pagination}
          onChange={(newPagination) => setPagination(newPagination)}
          scroll={{ x: 900 }}
          bordered={false}
          size="middle"
          showSorterTooltip={false}
        />
      </Card>

      {/* ───────── View User Drawer ───────── */}
      <Drawer
        title={
          viewUser ? (
            <Space>
              <Avatar
                size={36}
                src={viewUser.profileImage}
                icon={<UserOutlined />}
                style={{ backgroundColor: "#0A6EBD" }}
              />
              <div>
                <div style={{ fontWeight: 600 }}>{viewUser.name}</div>
                <div style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>
                  {viewUser.email}
                </div>
              </div>
            </Space>
          ) : (
            "User Details"
          )
        }
        open={viewDrawerOpen}
        onClose={() => setViewDrawerOpen(false)}
        width={600}
        loading={viewLoading}
        extra={
          viewUser && (
            <Tag color={roleColor(viewUser.role)}>
              {viewUser.role?.toUpperCase()}
            </Tag>
          )
        }
      >
        {viewUser && (
          <>
            {/* ── Account Status ── */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16} align="middle">
                <Col flex="auto">
                  <Space orientation="vertical" size={4}>
                    <Text strong>Account Status</Text>
                    <Space>
                      <Badge
                        status={viewUser.isActive !== false ? "success" : "error"}
                        text={
                          viewUser.isActive !== false ? "Active" : "Suspended"
                        }
                      />
                      <Tag
                        color={viewUser.isEmailVerified ? "blue" : "orange"}
                        icon={
                          viewUser.isEmailVerified ? (
                            <CheckCircleOutlined />
                          ) : null
                        }
                      >
                        {viewUser.isEmailVerified
                          ? "Email Verified"
                          : "Email Unverified"}
                      </Tag>
                    </Space>
                  </Space>
                </Col>
                <Col>
                  <Space orientation="vertical" size={8} style={{ textAlign: "right" }}>
                    <Popconfirm
                      title={
                        viewUser.isActive !== false
                          ? "Suspend this user?"
                          : "Unsuspend this user?"
                      }
                      description={
                        viewUser.isActive !== false
                          ? "The user will be blocked from logging in."
                          : "The user will regain access to their account."
                      }
                      onConfirm={handleSuspend}
                      okText="Confirm"
                      cancelText="Cancel"
                      okButtonProps={{
                        danger: viewUser.isActive !== false,
                      }}
                    >
                      <Button
                        danger={viewUser.isActive !== false}
                        type={viewUser.isActive !== false ? "default" : "primary"}
                        icon={
                          viewUser.isActive !== false ? (
                            <StopOutlined />
                          ) : (
                            <CheckCircleOutlined />
                          )
                        }
                        loading={actionLoading}
                        size="small"
                      >
                        {viewUser.isActive !== false ? "Suspend" : "Unsuspend"}
                      </Button>
                    </Popconfirm>

                    <Popconfirm
                      title={
                        viewUser.isEmailVerified
                          ? "Remove email verification?"
                          : "Manually verify this user's email?"
                      }
                      description={
                        viewUser.isEmailVerified
                          ? "User will need to verify their email again to login."
                          : "User's email will be marked as verified."
                      }
                      onConfirm={handleVerifyEmail}
                      okText="Confirm"
                      cancelText="Cancel"
                      okButtonProps={{ danger: viewUser.isEmailVerified }}
                    >
                      <Button
                        type={viewUser.isEmailVerified ? "default" : "primary"}
                        danger={viewUser.isEmailVerified}
                        icon={<SafetyCertificateOutlined />}
                        loading={actionLoading}
                        size="small"
                      >
                        {viewUser.isEmailVerified ? "Unverify Email" : "Verify Email"}
                      </Button>
                    </Popconfirm>
                  </Space>
                </Col>
              </Row>
            </Card>

            {/* ── Profile Photo ── */}
            {viewUser.profileImage && (
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <Image
                  src={viewUser.profileImage}
                  width={100}
                  height={100}
                  style={{ objectFit: "cover", borderRadius: "50%", border: "3px solid #0A6EBD" }}
                  preview={{ mask: "View Photo" }}
                />
                <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>Profile Photo</div>
              </div>
            )}

            {/* ── Basic Info ── */}
            <Descriptions
              title="Basic Information"
              column={1}
              size="small"
              bordered
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Account ID"><Text code copyable>{viewUser.publicId || viewUser._id}</Text></Descriptions.Item>
              <Descriptions.Item label="Address">{Object.values(viewUser.address || {}).filter(Boolean).join(", ") || "—"}</Descriptions.Item>
              <Descriptions.Item label="Medical history">{viewUser.medicalHistory || "—"}</Descriptions.Item>
              <Descriptions.Item label="Profile completion">{viewPatientProfile?.profileCompletion != null ? `${viewPatientProfile.profileCompletion}%` : "—"}</Descriptions.Item>
              <Descriptions.Item label="Name">{viewUser.name || "—"}</Descriptions.Item>
              <Descriptions.Item label="Email">{viewUser.email || "—"}</Descriptions.Item>
              <Descriptions.Item label="Phone">{viewUser.phone || "—"}</Descriptions.Item>
              <Descriptions.Item label="Role">
                <Tag color={roleColor(viewUser.role)}>{viewUser.role?.toUpperCase()}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Gender">
                {viewUser.gender
                  ? viewUser.gender.charAt(0).toUpperCase() + viewUser.gender.slice(1)
                  : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Age">{ageLabel(viewUser)}</Descriptions.Item>
              <Descriptions.Item label="Date of Birth">
                {viewUser.dateOfBirth
                  ? new Date(viewUser.dateOfBirth).toLocaleDateString("en-IN", {
                      day: "numeric", month: "short", year: "numeric",
                    })
                  : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Doctor Name">
                {viewUser.doctorName || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Hospital Name">
                {viewUser.hospitalName || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="State">
                {viewUser.state || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="District">
                {viewUser.district || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Pincode">
                {viewUser.pincode || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Categories">
                {(() => {
                  const cats = Array.isArray(viewUser.categories) && viewUser.categories.length > 0
                    ? viewUser.categories
                    : viewUser.category
                    ? [viewUser.category]
                    : [];
                  return cats.length > 0
                    ? <Space size={[0, 4]} wrap>{cats.map(c => <Tag key={c} color="orange">{c}</Tag>)}</Space>
                    : "—";
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Joined">
                {viewUser.createdAt
                  ? new Date(viewUser.createdAt).toLocaleDateString("en-IN", {
                      day: "numeric", month: "short", year: "numeric",
                    })
                  : "—"}
              </Descriptions.Item>
            </Descriptions>

            {profileError && <Alert type="warning" showIcon message={profileError} />}
            {/* ── Medical Info ── */}
            <>
              <Divider orientation="left">
                <Space><HeartOutlined style={{ color: "#f5222d" }} />Medical Information</Space>
              </Divider>
              <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Current Condition">
                  {viewPatientProfile?.medicalInfo?.currentCondition || "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Duration">
                  {(() => {
                    const y = viewPatientProfile?.medicalInfo?.duration?.years;
                    const m = viewPatientProfile?.medicalInfo?.duration?.months;
                    const parts = [y > 0 && `${y} yr`, m > 0 && `${m} mo`].filter(Boolean);
                    return parts.length > 0 ? parts.join(" ") : "—";
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label="Symptoms">
                  {viewPatientProfile?.medicalInfo?.symptoms || "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Allergies">
                  {viewPatientProfile?.healthDetails?.allergies || "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Medications">
                  {viewPatientProfile?.healthDetails?.currentMedications || "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Blood Group">
                  {viewPatientProfile?.healthDetails?.bloodGroup
                    ? <Tag color="red">{viewPatientProfile.healthDetails.bloodGroup}</Tag>
                    : "—"}
                </Descriptions.Item>
                {viewPatientProfile?.healthDetails?.height && (
                  <Descriptions.Item label="Height">
                    {viewPatientProfile.healthDetails.height} cm
                  </Descriptions.Item>
                )}
                {viewPatientProfile?.healthDetails?.weight && (
                  <Descriptions.Item label="Weight">
                    {viewPatientProfile.healthDetails.weight} kg
                  </Descriptions.Item>
                )}
              </Descriptions>
            </>

            {/* ── Emergency Contact ── */}
            {(viewPatientProfile?.emergencyContact?.name || viewPatientProfile?.emergencyContact?.phone) && (
              <>
                <Divider orientation="left">
                  <Space><UserOutlined style={{ color: "#0A6EBD" }} />Emergency Contact</Space>
                </Divider>
                <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="Name">
                    {viewPatientProfile.emergencyContact.name || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Relation">
                    {viewPatientProfile.emergencyContact.relation || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {viewPatientProfile.emergencyContact.phone || "—"}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}

            {/* ── Documents & Images ── */}
            <>
              <Divider orientation="left">
                <Space><IdcardOutlined style={{ color: "#1677ff" }} />Documents & Images</Space>
              </Divider>

              {/* Aadhar */}
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: "block", marginBottom: 8, fontSize: 13 }}>
                  🪪 Aadhar Card
                </Text>
                {(() => {
                  const docs = viewPatientProfile?.documents;
                  const front = docs?.aadharFront?.[0];
                  const back = docs?.aadharBack?.[0];
                  if (!front && !back) return <Text type="secondary" style={{ fontSize: 13 }}>Not uploaded</Text>;
                  return (
                    <Image.PreviewGroup>
                      <Space size={16}>
                        {front ? (
                          <div style={{ textAlign: "center" }}>
                            <Image src={front.fileUrl} width={140} height={90}
                              style={{ objectFit: "cover", borderRadius: 6, border: "1px solid #e8e8e8" }} />
                            <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>Front</Text>
                          </div>
                        ) : (
                          <div style={{ textAlign: "center", width: 140 }}>
                            <div style={{ width: 140, height: 90, border: "1px dashed #d9d9d9", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", color: "#bbb", fontSize: 12 }}>
                              Front not uploaded
                            </div>
                          </div>
                        )}
                        {back ? (
                          <div style={{ textAlign: "center" }}>
                            <Image src={back.fileUrl} width={140} height={90}
                              style={{ objectFit: "cover", borderRadius: 6, border: "1px solid #e8e8e8" }} />
                            <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>Back</Text>
                          </div>
                        ) : (
                          <div style={{ textAlign: "center", width: 140 }}>
                            <div style={{ width: 140, height: 90, border: "1px dashed #d9d9d9", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", color: "#bbb", fontSize: 12 }}>
                              Back not uploaded
                            </div>
                          </div>
                        )}
                      </Space>
                    </Image.PreviewGroup>
                  );
                })()}
              </div>

              {/* Test Reports */}
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: "block", marginBottom: 8, fontSize: 13 }}>
                  🧪 Test Reports ({viewPatientProfile?.documents?.testReports?.length || 0})
                </Text>
                {viewPatientProfile?.documents?.testReports?.length > 0 ? (
                  <Image.PreviewGroup>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {viewPatientProfile.documents.testReports.map((f, i) => (
                        f.fileType?.startsWith("image/") ? (
                          <div key={i} style={{ textAlign: "center" }}>
                            <Image src={f.fileUrl} width={80} height={80}
                              style={{ objectFit: "cover", borderRadius: 6, border: "1px solid #e8e8e8" }} />
                            <Text type="secondary" style={{ fontSize: 10, display: "block", marginTop: 2, maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.fileName}</Text>
                          </div>
                        ) : (
                          <a key={i} href={f.fileUrl} target="_blank" rel="noreferrer">
                            <div style={{ width: 80, height: 80, border: "1px solid #e8e8e8", borderRadius: 6, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, backgroundColor: "#f0f7ff" }}>
                              <FileOutlined style={{ fontSize: 22, color: "#1677ff" }} />
                              <span style={{ fontSize: 10, textAlign: "center", color: "#666", padding: "0 4px", wordBreak: "break-all" }}>{f.fileName}</span>
                            </div>
                          </a>
                        )
                      ))}
                    </div>
                  </Image.PreviewGroup>
                ) : <Text type="secondary" style={{ fontSize: 13 }}>No test reports uploaded</Text>}
              </div>

              {/* Prescriptions */}
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: "block", marginBottom: 8, fontSize: 13 }}>
                  📋 Prescriptions ({viewPatientProfile?.documents?.prescriptions?.length || 0})
                </Text>
                {viewPatientProfile?.documents?.prescriptions?.length > 0 ? (
                  <Image.PreviewGroup>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {viewPatientProfile.documents.prescriptions.map((f, i) => (
                        f.fileType?.startsWith("image/") ? (
                          <div key={i} style={{ textAlign: "center" }}>
                            <Image src={f.fileUrl} width={80} height={80}
                              style={{ objectFit: "cover", borderRadius: 6, border: "1px solid #e8e8e8" }} />
                            <Text type="secondary" style={{ fontSize: 10, display: "block", marginTop: 2, maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.fileName}</Text>
                          </div>
                        ) : (
                          <a key={i} href={f.fileUrl} target="_blank" rel="noreferrer">
                            <div style={{ width: 80, height: 80, border: "1px solid #e8e8e8", borderRadius: 6, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, backgroundColor: "#f6ffed" }}>
                              <FileOutlined style={{ fontSize: 22, color: "#0A6EBD" }} />
                              <span style={{ fontSize: 10, textAlign: "center", color: "#666", padding: "0 4px", wordBreak: "break-all" }}>{f.fileName}</span>
                            </div>
                          </a>
                        )
                      ))}
                    </div>
                  </Image.PreviewGroup>
                ) : <Text type="secondary" style={{ fontSize: 13 }}>No prescriptions uploaded</Text>}
              </div>

              {/* Body Proof */}
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: "block", marginBottom: 8, fontSize: 13 }}>
                  📸 Visual Evidence ({viewPatientProfile?.documents?.bodyProof?.length || 0})
                </Text>
                {viewPatientProfile?.documents?.bodyProof?.length > 0 ? (
                  <Image.PreviewGroup>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {viewPatientProfile.documents.bodyProof.map((f, i) => (
                        f.fileType?.startsWith("image/") ? (
                          <div key={i} style={{ textAlign: "center" }}>
                            <Image src={f.fileUrl} width={80} height={80}
                              style={{ objectFit: "cover", borderRadius: 6, border: "1px solid #e8e8e8" }} />
                            <Text type="secondary" style={{ fontSize: 10, display: "block", marginTop: 2, maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.fileName}</Text>
                          </div>
                        ) : (
                          <a key={i} href={f.fileUrl} target="_blank" rel="noreferrer">
                            <div style={{ width: 80, height: 80, border: "1px solid #e8e8e8", borderRadius: 6, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, backgroundColor: "#fff7e6" }}>
                              <FileOutlined style={{ fontSize: 22, color: "#0A6EBD" }} />
                              <span style={{ fontSize: 10, textAlign: "center", color: "#666", padding: "0 4px", wordBreak: "break-all" }}>{f.fileName}</span>
                            </div>
                          </a>
                        )
                      ))}
                    </div>
                  </Image.PreviewGroup>
                ) : <Text type="secondary" style={{ fontSize: 13 }}>No visual evidence uploaded</Text>}
              </div>
            </>

            {/* ── Doctor Referral Section ── */}
            {viewUser.role === "doctor" && (
              <>
                <Divider orientation="left">
                  <Space>
                    <GiftOutlined style={{ color: "#EB2F96" }} />
                    Doctor Referral
                  </Space>
                </Divider>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={12}>
                    <Card size="small" style={{ textAlign: "center", background: "#FFF0F6", border: "1px solid #ffadd2" }}>
                      <Statistic
                        title="Referral Points"
                        value={viewDoctorProfile?.referralPoints ?? 0}
                        prefix={<GiftOutlined />}
                        styles={{ content: { color: "#EB2F96" } }}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" style={{ textAlign: "center", background: "#FFF7E6", border: "1px solid #ffd591" }}>
                      <Statistic
                        title="Total Referrals"
                        value={viewDoctorProfile?.totalReferrals ?? 0}
                        prefix={<TeamOutlined />}
                        styles={{ content: { color: "#FA8C16" } }}
                      />
                    </Card>
                  </Col>
                </Row>
                <Card
                  size="small"
                  style={{ marginBottom: 16, background: "#F0EDFF", border: "1px dashed #667eea" }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>Referral Code</Text>
                      <div>
                        <Text strong style={{ fontSize: 22, letterSpacing: 4, color: "#667eea" }}>
                          {viewDoctorProfile?.referralCode || "—"}
                        </Text>
                      </div>
                    </div>
                    {viewDoctorProfile?.referralCode && (
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(viewDoctorProfile.referralCode)}
                      >
                        Copy
                      </Button>
                    )}
                  </div>
                </Card>
              </>
            )}

            {/* ── Membership Section ── */}
            {viewUser.membership && (
              <>
                <Divider orientation="left">
                  <Space>
                    <CrownOutlined style={{ color: "#d48806" }} />
                    Membership
                  </Space>
                </Divider>
                <Card
                  size="small"
                  style={{
                    marginBottom: 16,
                    background: viewUser.membership.isActive ? "#fffbe6" : "#fafafa",
                    border: `1px solid ${viewUser.membership.isActive ? "#faad14" : "#d9d9d9"}`,
                  }}
                >
                  <Row gutter={16} align="middle">
                    <Col flex="auto">
                      <Space orientation="vertical" size={4}>
                        <Space>
                          <CrownOutlined style={{ color: viewUser.membership.isActive ? "#d48806" : "#aaa" }} />
                          <Text strong style={{ color: viewUser.membership.isActive ? "#d48806" : "#aaa" }}>
                            {viewUser.membership.isActive
                              ? viewUser.membership.planName || "Active Plan"
                              : "No Active Membership"}
                          </Text>
                          <Tag color={viewUser.membership.isActive ? "gold" : "default"}>
                            {viewUser.membership.isActive ? "ACTIVE" : "INACTIVE"}
                          </Tag>
                        </Space>
                        {viewUser.membership.isActive && viewUser.membership.endDate && (
                          <Space>
                            <CalendarOutlined style={{ color: "#888" }} />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Expires:{" "}
                              {new Date(viewUser.membership.endDate).toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })}
                            </Text>
                          </Space>
                        )}
                        {viewUser.membership.membershipId && (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            ID: {viewUser.membership.membershipId}
                          </Text>
                        )}
                      </Space>
                    </Col>
                  </Row>
                </Card>
              </>
            )}

            {/* ── Referral Section ── */}
            <>
                <Divider orientation="left">
                  <Space>
                    <GiftOutlined style={{ color: "#0A6EBD" }} />
                    Referral Info
                  </Space>
                </Divider>

                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={12}>
                    <Card size="small" style={{ textAlign: "center" }}>
                      <Statistic
                        title="Total Referrals"
                        value={referralStats?.totalReferrals ?? 0}
                        prefix={<TeamOutlined />}
                        styles={{ content: { color: "#0A6EBD" } }}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" style={{ textAlign: "center" }}>
                      <Statistic
                        title="Points Balance"
                        value={viewUser.totalPoints ?? 0}
                        prefix={<GiftOutlined />}
                        styles={{ content: { color: "#0A6EBD" } }}
                      />
                    </Card>
                  </Col>
                </Row>

                {/* Referral Code */}
                <Card
                  size="small"
                  style={{
                    marginBottom: 16,
                    background: "#fffbe6",
                    border: "1px dashed #faad14",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Referral Code
                      </Text>
                      <div>
                        <Text
                          strong
                          style={{
                            fontSize: 20,
                            letterSpacing: 2,
                            color: "#d48806",
                          }}
                        >
                          {referralStats?.referralCode || "—"}
                        </Text>
                      </div>
                    </div>
                    {referralStats?.referralCode ? (
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() =>
                          copyToClipboard(referralStats.referralCode)
                        }
                      >
                        Copy
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        size="small"
                        loading={actionLoading}
                        onClick={handleGenerateReferralCode}
                      >
                        Generate
                      </Button>
                    )}
                  </div>
                </Card>

                {/* Referred Users List */}
                {referralStats?.referredUsers?.length > 0 && (
                  <>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                      Referred Users ({referralStats.totalReferrals})
                    </Text>
                    <List
                      size="small"
                      bordered
                      dataSource={referralStats.referredUsers}
                      style={{ marginBottom: 16 }}
                      renderItem={(u) => (
                        <List.Item
                          extra={
                            <Space size={4}>
                              <Tag
                                color={u.isEmailVerified ? "blue" : "orange"}
                                style={{ margin: 0 }}
                              >
                                {u.isEmailVerified ? "Verified" : "Pending"}
                              </Tag>
                              <Tag
                                color={u.isActive !== false ? "success" : "error"}
                                style={{ margin: 0 }}
                              >
                                {u.isActive !== false ? "Active" : "Suspended"}
                              </Tag>
                            </Space>
                          }
                        >
                          <List.Item.Meta
                            avatar={
                              <Avatar size={32} icon={<UserOutlined />} />
                            }
                            title={
                              <Text style={{ fontSize: 13 }}>{u.name}</Text>
                            }
                            description={
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {u.email}
                              </Text>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </>
                )}

                {referralStats?.totalReferrals === 0 && (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    No referrals yet.
                  </Text>
                )}
            </>
          </>
        )}
      </Drawer>

      {/* ───────── Create / Edit Modal ───────── */}
      <Modal
        title={editingUser ? "Edit User" : "Add New User"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: "Please enter name" }]}
          >
            <Input size="large" placeholder="Enter full name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Please enter valid email" },
            ]}
          >
            <Input size="large" placeholder="user@example.com" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[{ required: true, message: "Please enter phone" }]}
          >
            <Input size="large" placeholder="+91 9876543210" />
          </Form.Item>
          <Form.Item
            name="role"
            label="User Role"
            rules={[{ required: true, message: "Please select role" }]}
          >
            <Select size="large" placeholder="Select user role">
              <Option value="superadmin">Super Admin</Option>
              <Option value="doctor">Doctor</Option>
              <Option value="labadmin">Lab Admin</Option>
              <Option value="patient">Patient</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="password"
            label={editingUser ? "New Password (leave blank to keep current)" : "Password"}
            rules={[
              ...(!editingUser ? [{ required: true, message: "Please enter password" }] : []),
              { min: 8, message: "Password must be at least 8 characters" },
            ]}
          >
            <Input.Password
              size="large"
              placeholder={editingUser ? "Leave blank to keep current password" : "Enter password"}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                size="large"
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" size="large" htmlType="submit">
                {editingUser ? "Update User" : "Create User"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
